import { ProductType } from '@models/Product'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getPrograms, selectPrograms } from '@store/membership/ProgramReducer'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import dayjs, { Dayjs } from 'dayjs'
import { createContext, useContext, useEffect, useState } from 'react'

type DateRangeType = [Dayjs | null, Dayjs | null] | null

interface AnalyticsContextProps {
  selectedDates: [string, string] | undefined
  updateSelectedDates: (dates: DateRangeType) => void
  selectedProduct: ProductType
  updateSelectedProduct: (product: ProductType) => void
  selectedMembershipPrograms: number[] | undefined
  updateSelectedMembershipPrograms: (programs: number[]) => void
  /**
   * False only while a program-scoped provider is still resolving its default
   * program. Consumers must hold their request until it turns true: firing
   * earlier would send an unscoped, all-programs query whose answer is thrown
   * away as soon as the default lands.
   */
  isMembershipProgramReady: boolean
}

interface AnalyticsProviderProps {
  children: React.ReactNode
  /**
   * Membership analytics reads exactly one program at a time, because one
   * program means one billing currency and therefore aggregates that can be
   * summed. Left off by the venue and user providers, which have no program
   * dimension and must keep fetching unscoped.
   */
  membershipProgramScoped?: boolean
}

/**
 * The program the admin last picked on the membership analytics pages, kept
 * across reloads and pages. Storage can be unavailable (private mode, blocked
 * site data): every access is guarded and a failure just means "no stored
 * choice", i.e. the previous behaviour.
 */
const PROGRAM_STORAGE_KEY = 'bo.membership.analytics.programId'

const readStoredProgramId = (): number | undefined => {
  try {
    const raw = localStorage.getItem(PROGRAM_STORAGE_KEY)
    const id = raw === null ? NaN : Number(raw)
    return Number.isInteger(id) ? id : undefined
  } catch {
    return undefined
  }
}

const writeStoredProgramId = (id: number) => {
  try {
    localStorage.setItem(PROGRAM_STORAGE_KEY, String(id))
  } catch {
    // Storage unavailable: nothing to persist.
  }
}

const AnalyticsContext = createContext<AnalyticsContextProps | null>(null)

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  membershipProgramScoped = false,
}) => {
  const dispatch = useAppDispatch()
  const programs = useAppSelector(selectPrograms())

  const [selectedDates, setSelectedDates] =
    useState<AnalyticsContextProps['selectedDates']>(undefined)

  const [selectedProduct, setSelectedProduct] = useState<
    AnalyticsContextProps['selectedProduct']
  >(ProductType.All)

  const [selectedMembershipPrograms, setSelectedMembershipPrograms] =
    useState<AnalyticsContextProps['selectedMembershipPrograms']>(undefined)

  useEffect(() => {
    if (!membershipProgramScoped) return

    dispatch(getPrograms(initialQuery))
  }, [membershipProgramScoped, dispatch])

  // The selector's options are fetched inside SBAsyncSelect and never reach the
  // parent, so the default is resolved from the program store: the program the
  // admin picked last time when it is still one they can access, else the first
  // of the first page (and that fallback replaces the stale stored value).
  const programIds = programs.map((program) => program.id).join(',')

  useEffect(() => {
    if (!membershipProgramScoped || programs.length === 0) return

    const stored = readStoredProgramId()
    const initial =
      stored !== undefined && programs.some((program) => program.id === stored)
        ? stored
        : programs[0].id
    if (initial !== stored) writeStoredProgramId(initial)

    // Functional update so a program the user already picked survives a later
    // refresh of the program list.
    setSelectedMembershipPrograms((current) => current ?? [initial])
    // `programs` is read through `programIds`: the effect must follow the ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipProgramScoped, programIds])

  const formatDates = (dates: DateRangeType): [string, string] | undefined => {
    const dateFormat = 'YYYY-MM-DD'

    if (!dates) return

    const startDate = dayjs(dates[0]).format(dateFormat)
    const endDate = dayjs(dates[1]).format(dateFormat)

    return [startDate, endDate]
  }

  const valueContext = {
    selectedDates,
    updateSelectedDates: (dates: DateRangeType) => {
      setSelectedDates(formatDates(dates))
    },
    selectedProduct,
    updateSelectedProduct: (product: ProductType) => {
      setSelectedProduct(product)
    },
    selectedMembershipPrograms,
    updateSelectedMembershipPrograms: (programs: number[]) => {
      setSelectedMembershipPrograms(programs)
      if (programs[0] !== undefined) writeStoredProgramId(programs[0])
    },
    isMembershipProgramReady:
      !membershipProgramScoped || selectedMembershipPrograms !== undefined,
  }

  return (
    <AnalyticsContext.Provider value={valueContext}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext)
  if (!context)
    throw new Error('useAnalytics must be used within AnalyticsProvider')
  return context
}
